export interface News2Entry {
    tidpunkt_news2_bedomning?: string | null;
    respiratoryRate?: number | string | null;
    oxygenSaturation?: number | string | null;
    oxygenSaturation2?: number | string | null;
    oxygenSupply?: string | null;
    systolicBP?: number | string | null;
    diastolicBP?: number | string | null;
    pulse?: number | string | null;
    consciousness?: string | null;
    temperature?: number | string | null;
    news2_score?: number | null;
    risk_kategori?: string | null;
    fornyad_news2_bedomning?: string | null;
    avsteg_fran_fornyad_news2_bedomning?: string | null;
}

const NEWS2_INPUT_FIELDS: Array<keyof News2Entry> = [
    "tidpunkt_news2_bedomning",
    "respiratoryRate",
    "oxygenSaturation",
    "oxygenSaturation2",
    "oxygenSupply",
    "systolicBP",
    "diastolicBP",
    "pulse",
    "consciousness",
    "temperature",
    "fornyad_news2_bedomning",
    "avsteg_fran_fornyad_news2_bedomning",
];

export const NEWS2_REQUIRED_FIELDS: Array<keyof News2Entry> = [
    "respiratoryRate",
    "oxygenSaturation",
    "oxygenSupply",
    "systolicBP",
    "pulse",
    "consciousness",
    "temperature",
];

export const hasNews2Input = (entry?: News2Entry | null) =>
    NEWS2_INPUT_FIELDS.some((field) => {
        const value = entry?.[field];

        return value !== null && value !== undefined && value !== "";
    });

export const filterEmptyNews2Entries = (entries?: News2Entry[] | null) => (entries ?? []).filter(hasNews2Input);

const toNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const normalizedValue = typeof value === "string" ? value.replace(",", ".") : value;
    const parsedValue = Number(normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const scoreRespiratoryRate = (value: number) => {
    if (value <= 8) return 3;
    if (value <= 11) return 1;
    if (value <= 20) return 0;
    if (value <= 24) return 2;
    return 3;
};

const scoreOxygenSaturationScale1 = (value: number) => {
    if (value <= 91) return 3;
    if (value <= 93) return 2;
    if (value <= 95) return 1;
    return 0;
};

const scoreOxygenSaturationScale2 = (value: number, oxygenSupply?: string | null) => {
    const hasSupplementalOxygen = oxygenSupply === "Ja";

    if (value <= 83) return 3;
    if (value <= 85) return 2;
    if (value <= 87) return 1;
    if (value <= 92) return 0;

    if (!hasSupplementalOxygen) {
        return 0;
    }

    if (value <= 94) return 1;
    if (value <= 96) return 2;
    return 3;
};

const scoreOxygenSupply = (value?: string | null) => {
    if (value === "Ja") return 2;
    if (value === "Nej") return 0;
    return null;
};

const scoreSystolicBloodPressure = (value: number) => {
    if (value <= 90) return 3;
    if (value <= 100) return 2;
    if (value <= 110) return 1;
    if (value <= 219) return 0;
    return 3;
};

const scorePulse = (value: number) => {
    if (value <= 40) return 3;
    if (value <= 50) return 1;
    if (value <= 90) return 0;
    if (value <= 110) return 1;
    if (value <= 130) return 2;
    return 3;
};

const scoreConsciousness = (value?: string | null) => {
    if (!value) return null;
    return value === "A" ? 0 : 3;
};

const scoreTemperature = (value: number) => {
    if (value <= 35) return 3;
    if (value <= 36) return 1;
    if (value <= 38) return 0;
    if (value <= 39) return 1;
    return 2;
};

const calculateRiskCategory = ({ totalScore, hasSingleThree }: { totalScore: number; hasSingleThree: boolean }) => {
    if (totalScore >= 7) return "Hög";
    if (totalScore >= 5) return "Medium";
    if (hasSingleThree) return "Låg/Medium";
    return "Låg";
};

export const calculateNews2Bedomning = (currentBedomning?: News2Entry | null) => {
    if (!currentBedomning) {
        return {
            news2_score: null,
            risk_kategori: null,
        };
    }

    const respiratoryRate = toNumber(currentBedomning.respiratoryRate);
    const oxygenSaturation = toNumber(currentBedomning.oxygenSaturation);
    const oxygenSaturation2 = toNumber(currentBedomning.oxygenSaturation2);
    const systolicBP = toNumber(currentBedomning.systolicBP);
    const pulse = toNumber(currentBedomning.pulse);
    const temperature = toNumber(currentBedomning.temperature);

    const oxygenSupplyScore = scoreOxygenSupply(currentBedomning.oxygenSupply);
    const consciousnessScore = scoreConsciousness(currentBedomning.consciousness);

    const saturationScore =
        oxygenSaturation2 !== null
            ? scoreOxygenSaturationScale2(oxygenSaturation2, currentBedomning.oxygenSupply)
            : oxygenSaturation !== null
              ? scoreOxygenSaturationScale1(oxygenSaturation)
              : null;

    const scores = [
        respiratoryRate !== null ? scoreRespiratoryRate(respiratoryRate) : null,
        saturationScore,
        oxygenSupplyScore,
        systolicBP !== null ? scoreSystolicBloodPressure(systolicBP) : null,
        pulse !== null ? scorePulse(pulse) : null,
        consciousnessScore,
        temperature !== null ? scoreTemperature(temperature) : null,
    ];

    if (scores.some((score) => score === null)) {
        return {
            news2_score: null,
            risk_kategori: null,
        };
    }

    const normalizedScores = scores as number[];
    const totalScore = normalizedScores.reduce((sum, score) => sum + score, 0);

    return {
        news2_score: totalScore,
        risk_kategori: calculateRiskCategory({
            totalScore,
            hasSingleThree: normalizedScores.some((score) => score === 3),
        }),
    };
};
